"use client";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import { REST_API_URL } from "../api/config";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function ClientDropdown({ value, onChange }) {
  const [search, setSearch] = useState(value?.name || "");
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken")
      : null;

  useEffect(() => {
    if (typeof window !== "undefined") {
      const timeZone = getUserTimeZone();
      setUserTimeZone(timeZone);
    }
  }, []);

  const fetchClients = async () => {
    if (!token) return;
    try {
      setLoading(true);
      
      const headers = buildHeaders(token);
      
      const res = await axios.get(`${API_BASE}/client/clients-list/`, {
        headers,
      });

      if (Array.isArray(res.data)) {
        const formattedClients = res.data.map(client => {
          if (client.created_at || client.last_visit) {
            return {
              ...client,
              formatted_created_at: client.created_at ? 
                formatDateWithTimeZone(client.created_at) : null,
              formatted_last_visit: client.last_visit ? 
                formatDateWithTimeZone(client.last_visit) : null,
            };
          }
          return client;
        });
        
        setClients(formattedClients);
        setFiltered(formattedClients);
      }
    } catch (err) {
      console.error("❌ Failed to fetch clients:", err);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const formatDateWithTimeZone = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: userTimeZone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: userTimeZone,
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error("Error formatting time:", error);
      return "";
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: userTimeZone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  useEffect(() => {
    if (open) fetchClients();
  }, [open]);

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearch(term);

    if (!term.trim()) {
      setFiltered(clients);
      return;
    }

    const lower = term.toLowerCase();
    const filteredList = clients.filter((c) =>
      (c.name || "").toLowerCase().includes(lower)
    );
    setFiltered(filteredList);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setOpen(false);

      const trimmedSearch = search.trim();
      if (!trimmedSearch) return;

      let existingClient = clients.find(
        (c) => c.name.toLowerCase() === trimmedSearch.toLowerCase()
      );

      if (!existingClient && filtered.length === 1) {
        const match = filtered[0];
        if (match.name.toLowerCase().startsWith(trimmedSearch.toLowerCase())) {
          existingClient = match;
        }
      }

      if (existingClient) {
        onChange(existingClient.name, existingClient.id);
        setSearch(existingClient.name);
      } else {
        onChange(trimmedSearch, null);
      }
    }, 150);
  };

  return (
    <div className="relative w-full">
      <div className="appointment-page calendar-input">
        <input
          type="text"
          placeholder="Name Surname"
          value={search}
          onChange={handleSearch}
          onFocus={() => setOpen(true)}
          onBlur={handleBlur}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 outline-none"
        />
      </div>

      <AnimatePresence>
        {open && filtered.length > 0 && (
          <motion.ul
            className="absolute top-full left-0 w-full bg-white rounded-xl shadow-lg z-20 overflow-hidden mt-1 border border-gray-100 max-h-60 overflow-y-auto"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
          >
            {loading ? (
              <li className="p-3 text-center text-gray-500">Loading...</li>
            ) : (
              filtered.map((c) => (
                <li
                  key={c.id}
                  onClick={() => {
                    onChange(c.name, c.id);
                    setSearch(c.name);
                    setOpen(false);
                  }}
                  className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-800"
                >
                  <img
                    src={c.image_url || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                    alt={c.name}
                    className="w-8 h-8 rounded-full mr-3 object-cover"
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{c.name}</span>
                    {c.email && (
                      <span className="text-xs text-gray-500">{c.email}</span>
                    )}
                    {c.last_visit && (
                      <span className="text-xs text-gray-400 mt-1">
                        Last visit: {formatDateOnly(c.last_visit)}
                      </span>
                    )}
                  </div>
                </li>
              ))
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}